import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  InjectionToken,
  Input,
  OnInit, ViewChild,
  ViewEncapsulation
} from "@angular/core";
import {ComponentDefinition} from '@formbird/types';
import {ChangedDocumentService} from 'formbird-services';
import {SclUtilDocumentService} from '../../../../formbird-sc-shared/src/lib/scl-util-document.service';
import {FormbirdInjectorService} from '../../../../formbird-services/src/lib/services/injector/formbird-injector.service';
import {DeviceDetectService} from '../../../../formbird-sc-shared/src/lib/device-detect.service';
import {ComponentSetupService} from '../../../../formbird-sc-shared/src/lib/component-setup.service';
import {AbstractComponent} from '../../../../formbird-sc-shared/src/lib/components/abstract.component';
import {FinaliseValueFieldService} from '@services/document/finalise-value-field.service';
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

export const WINDOW_TOKEN = new InjectionToken<Window>('Window location object');
declare var global;
const logger = console;

interface DateTimeComponentDefinition extends ComponentDefinition {
  time_24hr?: boolean;
  caption?: string;
  style?: string;
  defaultNow?: string;
  noCalendar?: boolean;
  allowInput?: boolean;
  displayFormat?: string;
  finaliseValue?: any;
  simpleParse?: boolean;
}

interface DeviceDefinition {
  device: string,
  browser: string
}

@Component({
  templateUrl: './date-time.component.html',
  styleUrls: ['./date-time.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [{ provide: WINDOW_TOKEN, useValue: window }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateTimeComponent extends AbstractComponent<DateTimeComponentDefinition> implements OnInit {
  @Input() floatLabel: any;
  @ViewChild('textInput') textInput;
  @ViewChild('hiddenInput') hiddenInput;
  @ViewChild('dateInputRef') dateInputRef;

  dateInput = '';
  dateInputType = 'datetime-local';
  isMobileDetect: boolean;
  device: string;
  browser: string;

  modifiedFieldService: any;
  notificationService: any;
  currentDateStr;

  selectionStart = 0;
  selectionEnd = 3;

  allowInput = true;
  dateFormat = 'YYYY-MM-DDTHH:mm';
  displayFormat = 'DD MMMM YYYY [at] hh:mm A';
  displayDate = '';
  simpleParse = true;
  sequence = 'DMYHI';

  format = [
    {
      key: 'D',
      get: (day, date) => day ? date?.replace('DD', day?.padStart(2, '0')) : null
    },
    {
      key: 'M',
      get: (month, date) => month ? date?.replace('MM', month?.padStart(2, '0')) : null
    },
    {
      key: 'Y',
      get: (year, date) => {
        if( typeof year === 'undefined' || year?.length > 4) {
          return null;
        }

        let checkYear =  ((year * 1) % 10000).toString(); // get the last 4 or below digits from the year input
        if( checkYear.length === 1 ){
            checkYear = '0' + checkYear;
        }
        const newYear = dayjs().format('YYYY').substr(0,4 - checkYear.length) + checkYear;

        return date?.replace('YYYY', newYear);
      }
    },
    {
      key: 'H',
      get: (hour, date) => date?.replace('HH', hour?.padStart(2, '0') || '00')
    },
    {
      key: 'I',
      get: (minute, date) => date?.replace('mm', minute?.padStart(2, '0') || '00')
    }
  ];

  constructor(
    protected changedDocumentService: ChangedDocumentService,
    protected cdr: ChangeDetectorRef,
    protected componentSetupService: ComponentSetupService,
    private deviceDetectService: DeviceDetectService,
    private finaliseValueService: FinaliseValueFieldService,
    protected formbirdInjectorService: FormbirdInjectorService,
    private sclUtilDocument: SclUtilDocumentService,
  ) {
    super(formbirdInjectorService, changedDocumentService, componentSetupService, cdr);
  }

  ngOnInit() {
    super.ngOnInit();
    this.formControl = this.componentSetupService.setupStandardComponent(this);

    this.setupChangeDetection(true);

    this.displayFormat = this.componentDefinition.displayFormat || this.displayFormat;
    this.simpleParse = this.componentDefinition.simpleParse ?? this.simpleParse;

    if (!(typeof this.componentDefinition.allowInput === 'undefined' || this.componentDefinition.allowInput === null)) {
      this.allowInput = this.componentDefinition.allowInput;
    }


    if (this.componentDefinition.defaultValue) {
      const isNew = this.sclUtilDocument.isNew(this.document);
      if (isNew) {
        this.fieldValue = new Date(this.componentDefinition.defaultValue);
      }
    }

    const detect: boolean = this.deviceDetectService.detectMobile();
    const detectDevice : DeviceDefinition = this.deviceDetectService.detectBrowser();
    this.device = detectDevice?.device;
    this.browser = detectDevice?.browser;
    if (detect) {
      this.isMobileDetect = true;
    }

    if(this.componentDefinition.noCalendar) {
      this.dateInputType = 'time';
    }

    if (this.componentDefinition.defaultNow === 'New') {
      const isNew = this.sclUtilDocument.isNew(this.document);
      if (isNew && !this.fieldValue) {
        this.fieldValue = new Date();
        const defaultNowNewFinaliseValueFunction = (doc) => {
          doc[this.fieldName] = this.fieldValue;
          if (this.formControl) {
            this.formControl.patchValue(doc[this.fieldName], {emitEvent: false, onlySelf: true});
          }
        };
        this.finaliseValueService.addFinaliseValueFunction(this.template.documentId, this.fieldName, defaultNowNewFinaliseValueFunction);
      }
    } else if (this.componentDefinition.defaultNow === 'Save') {
      const scDateTimeFinaliseValueFunction = (doc) => {
        doc[this.fieldName] = new Date();
        if (this.formControl) {
          this.formControl.patchValue(doc[this.fieldName], {emitEvent: false, onlySelf: true});
        }
      };

      this.finaliseValueService.addFinaliseValueFunction(this.template.documentId, this.fieldName, scDateTimeFinaliseValueFunction);

    }

    if( this.fieldValue ){
      // const newTextDate = moment(this.fieldValue).local().format(this.displayFormat);
      const newTextDate = dayjs(this.fieldValue).format(this.displayFormat);

      this.displayDate = newTextDate;

      // this.dateInput = moment(this.fieldValue).local().format().slice(0, 16);
      this.dateInput = dayjs(this.fieldValue).format().slice(0, 16);
    }

    const compSub = this.changedDocumentService.watchComponentDef(this.fieldName, this.template.documentId).subscribe(tplItem => {
      this.onComponentDefChanged(tplItem);
    });
    this.subs.add(compSub);
  }


  showMobilePicker(event) {
    if (!this.isMobileDetect) return false;
    // this.showPicker(event);
  }

  showPicker(event) {
    event.preventDefault();
    if (this.isMobileDetect) {
      setTimeout(() => {
        this.dateInputRef.nativeElement.focus();
        switch (this.device){
          case 'Android':
            this.dateInputRef.nativeElement.click();
            break;
          default:
            this.dateInputRef.nativeElement.focus();
            break;
        }
      }, 100);
    } else {
      switch(this.browser){
        case 'Safari':
          this.dateInputRef.nativeElement.click();
          break;
        default:
          this.dateInputRef.nativeElement.showPicker();
          break;
      }
    }
  }

  onChangeHandler($event: Event) {
      const { noCalendar } = this.componentDefinition;
      const newValue = this.dateInputRef.nativeElement.value;
      let date = dayjs(newValue);
      if( noCalendar ){
        date = dayjs(newValue, 'HH:mm');
      }

      const datetimeUTC =  date.format();

      const newDate = date.isValid() ? date.format(this.displayFormat) : null;
      this.displayDate = newDate;
      // this.hiddenInput.nativeElement.value = datetimeUTC;

      this.valueChanged(datetimeUTC);
      this.onFieldValueChanged(datetimeUTC);
  }


  onFieldValueChanged(fieldValue) {
    if (this.fieldValue === fieldValue) {
      return;
    }

    this.fieldValue = fieldValue;
    this.formControl.patchValue(this.fieldValue, { emitEvent: false, onlySelf: true });
    this.detectChanges();
  }


  validateDate() {
    let newDate = dayjs(this.textInput.nativeElement.value, this.displayFormat, true);

    if( !newDate.isValid() && this.simpleParse) {
      const values = this.textInput.nativeElement.value.split(/\D/).filter(e => e);

      let dateFormat = this.dateFormat;
      for (const [key, item] of Array.from(this.sequence).entries()) {
        const dateFragment = this.format.find((format) => format.key === item);
        dateFormat = dateFragment.get(values[key], dateFormat);
      }

      if(dateFormat){
        newDate = dayjs(dateFormat);
      }
    }

    this.displayDate = newDate.isValid() ? newDate.format(this.displayFormat) : null; // input visible, formatted date, no timezone
    this.dateInput = newDate.isValid() ? newDate.format() : null; // date input, no timzeone

    this.valueChanged(this.dateInput);
    this.onFieldValueChanged(this.dateInput);
  }


  valueChanged(newValue) {
    if (newValue) {
      const newFieldValueStr = new Date(newValue).toISOString();
      const newFieldValueDate = dayjs(newFieldValueStr).utc().format();
      super.valueChanged(newFieldValueDate);
      this.currentDateStr = newFieldValueDate;

    } else {
      delete this.document[this.fieldName];
      super.valueChanged(undefined);
      this.currentDateStr = null;
    }
  }
}
